import React from 'react';
import { useSelector } from 'react-redux';

import { ISubscription } from 'redux/reducers/subscriptions';

import { ReactComponent as Icon } from 'icons/icon-chevron.svg';
import { ReactComponent as SettingsIcon } from 'icons/icon-notification-settings.svg';

import UnreadEventsList from './EventList';
import { IArtist, IGroupedEvents } from '../types';
import { RootState } from 'redux/rootReducer';

import {
  NoDataMessage,
  Scroll,
  Fixed,
  ArtistLink,
  Artwork,
  Flex,
} from '../styles';

import Box from 'components/elements/Box';
import Button from '../../../elements/button';
import { setShowArtistsOverview } from '../../../../redux/reducers/notificationModals';
import { useDispatch } from 'react-redux';
import EventsSkeletonLoader from './SkeletonLoader';
import {
  Link,
  LinkSpan,
} from 'components/data-containers/PlaylistPriorityActivity/styles';
import checkBetaGroup from 'helpers/checkBetaGroup';

const UnreadEvents: React.FC<IUnreadEvents> = (props) => {
  const {
    subscriptions,
    groupedUnreadEvents,
    countries,
    artistsMetadata,
    setDetailPageEntityId,
    handleAcknowledgeEvent,
    handleAcknowledgeAllEvents,
    setIsOpenEvents,
    isMobileScreen,
    isPending,
    regions,
  } = props;

  const dispatch = useDispatch();

  const isOpusNotifierOn = useSelector(
    (state: RootState) => state.consulEnvData?.data?.isOpusNotifierOn
  );
  const BetaGroupPriorityPlaylist = useSelector(
    (state: RootState) => state.consulEnvData?.data?.BetaGroupPriorityPlaylist
  );
  const authenticationProfile = useSelector(
    (state: RootState) => state.authentication.profile
  );

  const showOpusNotifier =
    isOpusNotifierOn &&
    checkBetaGroup(BetaGroupPriorityPlaylist, authenticationProfile?.email);

  function openOverview(): void {
    dispatch(setShowArtistsOverview());
    setIsOpenEvents(false);
  }

  function sortByName(first: IArtist, second: IArtist): number {
    return first.entity.displayName.localeCompare(second.entity.displayName);
  }

  function sortByHasEvents(first: IArtist, second: IArtist): number {
    if (!groupedUnreadEvents) return 0;

    const firstId = first.entity.id;
    const secondId = second.entity.id;
    const hasFirst = firstId in groupedUnreadEvents;
    const hasSecond = secondId in groupedUnreadEvents;

    if (hasFirst && !hasSecond) {
      return -1;
    }
    if (!hasFirst && hasSecond) {
      return 1;
    }

    return 0;
  }

  if (
    !subscriptions ||
    subscriptions.length === 0 ||
    !artistsMetadata ||
    !artistsMetadata.length ||
    isPending
  ) {
    return (
      <>
        <Fixed>
          <Box display={'flex'} alignItems={'baseline'}>
            <h2>What&apos;s happening?</h2>

            <Button onClick={openOverview} display="flex" marginLeft="auto">
              <SettingsIcon />
            </Button>
          </Box>
          <Box
            display={'flex'}
            alignItems={'baseline'}
            justifyContent={'space-between'}
          >
            {isPending && <EventsSkeletonLoader />}
            {showOpusNotifier && (
              <Link to="/downloadOpusNotifierApp">
                <LinkSpan>Interested in mobile app?</LinkSpan>
              </Link>
            )}
          </Box>
        </Fixed>

        {!isPending && (
          <Scroll>
            <Box px={4}>
              <NoDataMessage>
                You are not subscribed to any artist.
              </NoDataMessage>
              <NoDataMessage>
                Start receiving notifications by subscribing to an artist on
                their artist page.
              </NoDataMessage>
            </Box>
          </Scroll>
        )}
      </>
    );
  }

  return (
    <>
      <Fixed>
        <Box display={'flex'} alignItems={'baseline'}>
          <h2>What&apos;s happening?</h2>

          <Button onClick={openOverview} display="flex" marginLeft="auto">
            <SettingsIcon />
          </Button>
        </Box>
        <Box
          display={'flex'}
          alignItems={'baseline'}
          justifyContent={'space-between'}
        >
          {!groupedUnreadEvents ? (
            <NoDataMessage>No new notifications.</NoDataMessage>
          ) : (
            <Button
              data-gtag="notifications-popout-mark-all-as-read"
              type="button"
              onClick={handleAcknowledgeAllEvents}
              color="AccentGreen"
              m={0}
              px={0}
              py={1}
            >
              Mark all as read
            </Button>
          )}
          {showOpusNotifier && (
            <Link to="/downloadOpusNotifierApp">
              <LinkSpan>Interested in mobile app?</LinkSpan>
            </Link>
          )}
        </Box>
      </Fixed>

      <Scroll isMobileScreen={isMobileScreen}>
        {artistsMetadata
          .sort(sortByName)
          .sort(sortByHasEvents)
          .map((artist: IArtist) => {
            const { displayName, id, thumbnailUrl } = artist.entity;
            const artistHasEvents =
              groupedUnreadEvents && id in groupedUnreadEvents;
            const events = groupedUnreadEvents?.[id];
            const eventCount = events?.length;

            return (
              <React.Fragment key={id}>
                <ArtistLink
                  type="button"
                  onClick={(evt: MouseEvent): void => {
                    evt.stopPropagation();
                    setDetailPageEntityId(id);
                  }}
                >
                  <Flex>
                    <Artwork src={thumbnailUrl} entityType="artist" />
                    <h3>
                      {displayName}
                      {eventCount && <span> ({eventCount})</span>}
                    </h3>
                  </Flex>
                  <Icon aria-hidden />
                </ArtistLink>

                {artistHasEvents && (
                  <UnreadEventsList
                    countries={countries}
                    events={events}
                    setDetailPageEntityId={setDetailPageEntityId}
                    handleAcknowledgeEvent={handleAcknowledgeEvent}
                    regions={regions}
                  />
                )}
              </React.Fragment>
            );
          })}
      </Scroll>
    </>
  );
};

interface IUnreadEvents {
  subscriptions: null | Array<ISubscription>;
  groupedUnreadEvents?: IGroupedEvents;
  countries: any[];
  artistsMetadata: null | IArtist[];
  setDetailPageEntityId: (entityId: string) => void;
  handleAcknowledgeEvent: (eventId: number) => void;
  handleAcknowledgeAllEvents: () => void;
  setIsOpenEvents: (status: boolean) => void;
  isPending: boolean;
  isMobileScreen?: boolean;
  regions: any[];
}

export default UnreadEvents;
